/**
 * Browser-level error hooks — the safety net underneath Angular's own
 * ErrorHandler (see AppErrorHandler).
 *
 * AppErrorHandler only ever sees errors thrown INSIDE Angular's zone. Three
 * common failure modes escape it entirely, and each one presents as "the app
 * stopped working and the console is empty":
 *
 *   1. Errors thrown from code that left the zone (third-party library
 *      callbacks, `setTimeout` inside a vendor bundle, chart plugins).
 *   2. Rejected promises with no `.catch()` — silently swallowed by default.
 *   3. Anything thrown before Angular finishes bootstrapping.
 *
 * Everything here is deliberately defensive: a diagnostic that can itself
 * throw is worse than no diagnostic, since it would mask the very error it
 * exists to surface.
 */

import { renderDiagnosticsSnapshot } from './render-diagnostics';

/** Marker on every log this file emits, so it's greppable in a noisy prod console. */
const PREFIX = '[GlobalError]';

let installed = false;

/**
 * Idempotent: bootstrapping twice (or a hot reload) must not stack duplicate
 * handlers, which would print every error two or more times.
 */
export function installGlobalErrorHooks(): void {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  // Chained rather than assigned to window.onerror, so an existing handler
  // (or one a vendor script installs later) keeps working.
  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      // Resource load failures (a 404'd <img>/<script>) surface here too, but
      // carry no `error` and target an element rather than the window — worth
      // reporting, but distinctly, since they are not exceptions.
      if (event?.target && event.target !== window) {
        const el = event.target as HTMLElement;
        console.error(`${PREFIX} resource failed to load:`, {
          tag: el?.tagName,
          src: (el as HTMLImageElement | HTMLScriptElement)?.['src' as keyof HTMLElement],
        });
        return;
      }

      console.error(`${PREFIX} uncaught exception:`, {
        message: event?.message,
        source: event?.filename,
        line: event?.lineno,
        column: event?.colno,
        // The Error object carries the stack; sourcemaps are enabled in every
        // build configuration, so this resolves to real file/line numbers.
        error: event?.error,
        stack: event?.error?.stack,
      });
    } catch {
      /* never let the reporter break the page */
    }
  }, true); // capture phase — resource errors don't bubble

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      const reason: any = event?.reason;
      console.error(`${PREFIX} unhandled promise rejection:`, {
        reason,
        message: reason?.message ?? String(reason),
        stack: reason?.stack,
        // An HttpErrorResponse that nothing subscribed an error handler to
        // lands here — url/status make that case immediately recognisable.
        url: reason?.url,
        status: reason?.status,
      });
    } catch {
      /* never let the reporter break the page */
    }
  });

  // Exposed for console use during an investigation: run __renderDiag() in
  // the prod console to see how often the instrumented getters/change-detection
  // hooks are firing (see render-diagnostics.ts).
  try {
    (window as any).__renderDiag = renderDiagnosticsSnapshot;
  } catch {
    /* non-fatal — the hooks above are the important part */
  }

  console.info(`${PREFIX} hooks installed`);
}
